/**
 * Warehouse Details
 */

// React
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

//UI
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  CircularProgress,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiKeyOutline,
  mdiArrowRight,
  mdiPhoneOutline,
  mdiCounter,
  mdiCalendar,
  mdiCalendarClock,
  mdiHomeCityOutline,
  mdiMapMarkerOutline,
  mdiWarehouse,
  mdiAccountArrowLeftOutline,
} from '@mdi/js';

// Components
import ListUsers from './list';

// Helper
import {
  formaterPhoneNumber,
} from '../../utils/helpers';

// Type
import { Props } from './interfaces';
import handleError from "../../utils/handleError";
import userRepository from "client-sdk/src/Repository/users-repository";
import warehouseRepository from "client-sdk/src/Repository/warehouses-repository";

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        '@media (max-width: 768px)': {
          margin: theme.spacing(0),
        },
      },
    },
    paper: {
      marginTop: '36px',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
      '@media (max-width: 768px)': {
        padding: theme.spacing(0),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
      },
    },

    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 500,
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    divider: {
      width: '98%',
      margin: '10px auto',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '10px',
    },
    headerBox: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '28px',
    },
    contentlabelTitle: {
      position: 'relative',
      color: theme.palette.gray.light,
      marginLeft: '40px',
      fontSize: '14px',
      fontWeight: 500,
      '&:before': {
        content: '"-"',
        position: 'absolute',
        left: '-20px',
      },
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
  })
);

export function Details(props: Props) {


  const [warehouse, setWarehouse] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const classes = useStyles();

  const getWarehouse = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } = await warehouseRepository.getWarehouses({ payload });
      setWarehouses(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getUsers = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } = await userRepository.getUsers({ payload });
      setUsers(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // @ts-ignore
    getWarehouses();
    getUsers({
      'warehousesId.equals': props.match.params.id,
    });
    getWarehouse({
      size: 100
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getWarehousesWithId = async (id: any) => {
    const payload = { id };
    try {
      setLoading(true);
      const response = await warehouseRepository.getWarehouseWithId({ payload });
      setWarehouse(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  }

  const getWarehouses = async () => {
    const { id } = props.match.params;
    await getWarehousesWithId(id);
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerBox}>
        <Typography variant="h6" component="h2">
          {`
        جزيیات انبار
        ${warehouse.name}
     
        `}
        </Typography>
        <span className={classes.contentlabelTitle}>{warehouse.id}</span>
      </div>
      <Grid container spacing={6}>
        <Grid xs={6} item container alignItems="center">
          <Button
            aria-label="details"
            size="medium"
            onClick={() => window.history.back()}
          >
            <Icon path={mdiArrowRight} size={1} className={classes.icon} />
            {`بازگشت`}
          </Button>
        </Grid>
      </Grid>

      <Paper elevation={3} className={classes.paper} variant="elevation" square>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiWarehouse} size={1} className={classes.icon} />
                {`نام انبار:  `}
              </span>

              <span>{warehouse.name}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiKeyOutline} size={1} className={classes.icon} />
                {`شناسه انبار:  `}
              </span>
              <span>{warehouse.id}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiPhoneOutline}
                  size={1}
                  className={classes.icon}
                />
                {` شماره تلفن:  `}
              </span>
              <span>{formaterPhoneNumber(warehouse.phoneNumber)}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCounter} size={1} className={classes.icon} />
                {`ظرفیت:  `}
              </span>
              <span>{warehouse.capacity}</span>
            </div>
          </Grid>

          <Divider className={classes.divider} />
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCalendar} size={1} className={classes.icon} />
                {`: روزهای فعالیت `}
              </span>
              <span>{warehouse.workDays}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiCalendarClock}
                  size={1}
                  className={classes.icon}
                />
                {`: ساعت فعالیت `}
              </span>
              <span>{warehouse.workTimes}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiHomeCityOutline}
                  size={1}
                  className={classes.icon}
                />
                {`استان :  `}
              </span>
              <span>{warehouse.province}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiHomeCityOutline}
                  size={1}
                  className={classes.icon}
                />
                {`شهر:  `}
              </span>
              <span>{warehouse.city}</span>
            </div>
          </Grid>
          {warehouse.parentId && (
            <>
              <Divider className={classes.divider} />
              <Grid item xs={12} sm={12}>
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon
                      path={mdiHomeCityOutline}
                      size={1}
                      className={classes.icon}
                    />
                    {`والد انبار:  `}
                  </span>
                  <span>
                    {
                      props?.warehouses?.find(
                        item => item?.id === warehouse?.parentId
                      )?.name
                    }
                  </span>
                </div>
              </Grid>
            </>
          )}

          <Divider className={classes.divider} />
          <Grid item xs={12} sm={12}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiMapMarkerOutline}
                  size={1}
                  className={classes.icon}
                />
                {`: آدرس `}
              </span>
              <span>{warehouse.address}</span>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid container item xs={12} direction="row-reverse">
          <Link
            style={{ textDecoration: 'none' }}
            to={`/warehouses/assign-user/${warehouse.id}`}
          >
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<Icon path={mdiAccountArrowLeftOutline} size={1} />}
            >
              {`اختصاص کاربر به انبار`}
            </Button>
          </Link>
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            users && <ListUsers data={users} />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

// const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
//   warehouse: makeSelectWarehouse(),
//   warehouses: makeSelectList(),
//   users: makeSelectUsersList(),
//   loading: makeSelectLoading(),
//   error: makeSelectError(),
// });

// function mapDispatchToProps(dispatch: Function) {
//   return {
//     getWarehousesWithIdDispatch: (id: number) =>
//       dispatch(getWarehouseWithIdAction({id})),
//     warehousesDispatch: (payload: Object) =>
//       dispatch(getWarehousesAction(payload)),
//     getUsersDispatch: (payload: object) => dispatch(getUsersAction(payload)),
//   };
// }

// const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default Details;
